import ReactGA from 'react-ga4';

// Check if analytics should be enabled in the current environment
const isAnalyticsEnabled = (): boolean => {
  return process.env.NODE_ENV === 'production' || 
         process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV === 'true';
};

// Initialize Google Analytics with your Measurement ID
export const initGA = (measurementId: string): void => {
  if (isAnalyticsEnabled()) {
    ReactGA.initialize(measurementId);
  } else {
  }
};

// Track page views
export const trackPageView = (path: string): void => {
  if (isAnalyticsEnabled()) {
    ReactGA.send({ hitType: 'pageview', page: path });
  }
};

// Chatbar personalization events based on tagging plan
export const trackChatbarPersonalization = (
  eventType: 'intro' | 'fam' | 'colour' | 'theme' | 'type' | 'managed' | 'procho',
  clickValue: string
): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'chatbar';
  const source = 'personalisation';
  const action = 'click';
  const eventName = `chat-${section}-${source}-${action}-${eventType}`;
  
  // Set up properties based on eventType as defined in the tagging plan
  let clickProperty = clickValue;

  // Special handling for different event types based on the tagging plan
  switch (eventType) {
    case 'intro':
    case 'colour':
      // For intro and colour: "click = yes | no"
      clickProperty = clickValue.toLowerCase() === 'yes' ? 'yes' : 'no';
      break;
    case 'fam':
      // For fam: "click = fam | other"
      clickProperty = clickValue.toLowerCase() === 'fam' ? 'fam' : 'other';
      break;
    case 'theme':
      // For theme: "click = eclipse | light | dark"
      if (['eclipse', 'light', 'dark'].includes(clickValue.toLowerCase())) {
        clickProperty = clickValue.toLowerCase();
      }
      break;
    case 'type':
      // For type: "click = participant | family | supco | provider | other"
      if (clickValue.toLowerCase().includes('participant')) {
        clickProperty = 'participant';
      } else if (clickValue.toLowerCase().includes('family') || clickValue.toLowerCase().includes('carer')) {
        clickProperty = 'family';
      } else if (clickValue.toLowerCase().includes('coordinator')) {
        clickProperty = 'supco';
      } else if (clickValue.toLowerCase().includes('provider')) {
        clickProperty = 'provider';
      } else {
        clickProperty = 'other';
      }
      break;
    case 'managed':
      // For managed: "click = plan | agency | self | other"
      if (clickValue.toLowerCase().includes('plan')) {
        clickProperty = 'plan';
      } else if (clickValue.toLowerCase().includes('agency')) {
        clickProperty = 'agency';
      } else if (clickValue.toLowerCase().includes('self')) {
        clickProperty = 'self';
      } else {
        clickProperty = 'other';
      }
      break;
    case 'procho':
      // For procho: "click = procho | other"
      if (clickValue.toLowerCase().includes('provider choice')) {
        clickProperty = 'procho';
      } else {
        clickProperty = 'other';
      }
      break;
  }

  // Using only ReactGA.gtag for tracking to prevent duplicates
  ReactGA.gtag('event', eventName, {
    click: clickProperty
  });

};

// Plan upload tracking events based on tagging plan
export const trackPlanUploadClick = (): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'chatbar';
  const source = 'plan upload';
  const action = 'click';
  const eventName = `chat-${section}-${source}-${action}`;
  
  ReactGA.gtag('event', eventName);

};

export const trackPlanUploadSuccess = (success: boolean, errorType?: string): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'chatbar';
  const source = 'plan upload';
  const action = 'success';
  const eventName = `chat-${section}-${source}-${action}`;
  
  if (success) {
    ReactGA.gtag('event', eventName, {
      value: 'true'
    });
  } else {
    ReactGA.gtag('event', eventName, {
      value: 'false',
      error_type: errorType || 'unknown'
    });
  }
};

// Profile menu tracking events based on tagging plan
export const trackProfileMenuClick = (
  source: 'account settings' | 'delete plan' | 'clear chat' | 'logout' | 'terms'
): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'profile menu';
  const action = 'click';
  const eventName = `chat-${section}-${source}-${action}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName);


};

// Account settings tracking events based on tagging plan
export const trackAccountSettingsClose = (): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'account settings';
  const action = 'close-click';
  const eventName = `chat-${section}-${action}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName);


};

export const trackAccountSettingsSave = (
  eventType: 'name' | 'avatar',
  changed: boolean
): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'account settings';
  const action = 'save-click';
  const eventName = `chat-${section}-${action}-${eventType}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName, {
    value: changed ? 'true' : 'false'
  });


};

// Track when user clicks logout button in account settings
export const trackAccountSettingsLogout = (): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'account settings';
  const action = 'logout-click';
  const eventName = `chat-${section}-${action}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName);


};

// PII form tracking based on tagging plan
export const trackPIIFormUserSelector = (click: 'participant' | 'nominee'): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'pii form';
  const source = 'user selector';
  const action = 'click';
  const eventName = `chat-${section}-${source}-${action}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName, {
    click: click
  });


};

export const trackPIIFormConfirm = (
  field: 'p name' | 'p ndis number' | 'p address' | 'n name' | 'n ndis number',
  changed: boolean
): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'pii form';
  const source = 'confirm';
  const action = 'click';
  const eventName = `chat-${section}-${source}-${action}-${field.replace(' ', ' ')}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName, {
    value: changed ? 'true' : 'false'
  });


};

export const trackPIIFormClose = (): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'pii form';
  const source = 'close';
  const action = 'click';
  const eventName = `chat-${section}-${source}-${action}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName);


};

// Confirmation alert tracking events based on tagging plan
export const trackConfirmationAlertClick = (
  source: 'plan' | 'logout',
  confirmed: boolean
): void => {
  if (!isAnalyticsEnabled()) return;

  const section = 'confirmation alert';
  const action = 'click';
  const eventName = `chat-${section}-${source}-${action}`;
  
  // Using only ReactGA.gtag for event tracking to prevent duplicates
  ReactGA.gtag('event', eventName, {
    click: confirmed ? 'yes' : 'no'
  });


};
